import type { EntitlementSnapshot } from './subscriptions';
import type { SignedKey } from './signedKey';

// ─── Vault Item Types ─────────────────────────────────────────────────────────

/**
 * Typed identifier for matching a vault item to a web origin or native app.
 * Replaces the legacy single-string `url` field on VaultItem. Existing items
 * with only `url` set continue to be read correctly via `effectiveWebIdentifiers`.
 */
export type Identifier =
  | { type: 'web'; value: string }            // e.g. 'https://capitalone.com'
  | { type: 'android-app'; value: string };   // e.g. 'com.konylabs.capitalone'

export type VaultItemType =
  | 'login'
  | 'note'
  | 'card'
  | 'identity'
  | 'passkey'
  // ── Binder (family knowledge) item types — see the Binder data-model spec.
  | 'contact'
  | 'document'
  | 'schedule'
  | 'list'; // collaborative family checklist — see the Binder-lists spec

// ─── Binder supporting types (encrypted inside the item JSON) ─────────────────

/** A labelled contact value — phone number, email, or postal address. `label`
 *  is freeform (suggested values like 'mobile'/'home'/'work' for phones). */
export interface ContactField {
  label: string;
  value: string;
}

/** A labelled link on a document. */
export interface DocumentLink {
  label?: string;
  url: string;
}

/**
 * One entry on a Binder list (collaborative family checklist). Lives inside the
 * owning list item's encrypted JSON (`VaultItem.listItems`). Because shared-item
 * sync is blind whole-item last-write-wins, concurrent edits are reconciled by a
 * per-entry merge (`listMerge.ts`): entries union by `id`, `done` is last-write
 * -wins by `doneAt`, and removal is a terminal tombstone. Attribution ids are
 * `OrgMember.userId`, resolved to display names via the family roster.
 * See the Binder-lists spec.
 */
export interface ListEntry {
  /** Stable uuid, assigned once at add time; the merge key. */
  id: string;
  /** Item text. Immutable after creation in v1 (to edit, remove and re-add). */
  text: string;
  /** userId of the member who added the item. */
  addedBy: string;
  /** ISO-8601 instant the item was added. */
  addedAt: string;
  /** Current done state. */
  done: boolean;
  /** userId of the member whose toggle produced the current `done` value. */
  doneBy?: string;
  /** ISO-8601 instant of the last done-state change — the LWW key for `done`. */
  doneAt?: string;
  /** Tombstone. Once true it is terminal: a removed entry never comes back. */
  deleted?: boolean;
  /** userId of the member who removed the item. */
  deletedBy?: string;
  /** ISO-8601 instant of removal. */
  deletedAt?: string;
  /** Fractional-index rank key (base-62 ASCII); entries display in ascending
   *  lexicographic `position` order. Absent on pre-reorder-feature entries, which
   *  fall back to `addedAt` order. See `fractionalIndex.ts` and the list-reorder spec. */
  position?: string;
  /** ISO-8601 instant of the last position change — the LWW key for `position`. */
  positionedAt?: string;
  /** userId whose move produced the current `position` (attribution; not surfaced). */
  positionedBy?: string;
}

/**
 * Reference to an end-to-end-encrypted image attachment stored in S3. Inlined in
 * the owning item's encrypted JSON (documents: `attachments[]`; contacts:
 * `avatar`) — never a standalone DynamoDB record. The bytes in S3 are AES-256-GCM
 * ciphertext under a per-attachment content key that is itself ECIES-wrapped to
 * the family public key, so the server is blind to the image. See spec §5.3/§6.
 */
export interface AttachmentRef {
  attachmentId: string;
  /** Plaintext, structural: "org/<orgId>/<itemId>/<attachmentId>". */
  s3Key: string;
  /** Per-attachment AES key, ECIES-wrapped to the family public key. */
  wrappedContentKey: string;
  wrappedContentKeyIv: string;
  /** ECIES ephemeral pubkey for the content-key wrap (plaintext, as elsewhere). */
  ephemeralPublicKey: string;
  filename: string;
  /** image/* only, day one. */
  mime: string;
  size: number;
  /** s3Key of the encrypted, client-generated thumbnail, when present. */
  thumbnailKey?: string;
}

/**
 * A user-defined extra field stored on a vault item, e.g. an AWS account ID
 * or a security-question answer. `name` doubles as the autofill match hint:
 * the extension scores page inputs against it. `hidden` marks the value as a
 * secret — masked in detail views and revealed on demand, like a password.
 */
export interface CustomField {
  name: string;
  value: string;
  hidden?: boolean;
}

/**
 * One previously-used password for a login item. The CURRENT password stays in
 * `VaultItem.password`; on a password change the outgoing password is prepended
 * to `VaultItem.passwordHistory` with `replacedAt = now`, and the list is capped
 * at the four most recent (oldest dropped). Lives inside the encrypted item JSON,
 * so the server never sees it. Like `customFields`, every client MUST preserve
 * this on save even without UI, or it is silently dropped on re-encrypt.
 */
export interface PasswordHistoryEntry {
  /** A password this item previously held. */
  password: string;
  /** ISO-8601 timestamp when this password stopped being current (was replaced). */
  replacedAt: string;
}

/** How many previous passwords a vault item retains. */
export const MAX_PASSWORD_HISTORY = 4;

/**
 * Compute the updated `passwordHistory` for a login item whose password is
 * changing from `oldPassword` to `newPassword`. The outgoing password is
 * prepended (newest first) with `replacedAt = now`, and the list is truncated to
 * the {@link MAX_PASSWORD_HISTORY} most recent. Returns `history` unchanged when
 * there is nothing to retire — no old password, or the password didn't actually
 * change — so callers can run it unconditionally on save.
 *
 * Pure and client-agnostic (extension today; web/native can reuse it). It only
 * decides the list contents; the current password still lives in
 * `VaultItem.password`.
 */
export function rotatePasswordHistory(
  history: PasswordHistoryEntry[] | undefined,
  oldPassword: string | null | undefined,
  newPassword: string,
  now: string = new Date().toISOString(),
): PasswordHistoryEntry[] | undefined {
  if (!oldPassword || oldPassword === newPassword) return history;
  return [{ password: oldPassword, replacedAt: now }, ...(history ?? [])]
    .slice(0, MAX_PASSWORD_HISTORY);
}

export interface Passkey {
  /** base64url of the credential ID (32 random bytes). */
  credentialId: string;
  publicKeyJwk: JsonWebKey;
  /** Discoverable-credential v1: we always hold the private key. */
  privateKeyJwk: JsonWebKey;
  /** base64url. The opaque user handle the RP gave us at registration. */
  userHandle: string;
  userName: string;
  userDisplayName: string;
  /** COSE algorithm id. ES256 only in v1. */
  algorithm: -7;
  createdAt: string;
  /** ISO 8601 — updated on each successful assertion. */
  lastUsedAt?: string;
}

/** Plaintext vault item — only ever lives in memory, never persisted as-is */
export interface VaultItem {
  id: string;
  type: VaultItemType;
  name: string;
  // Login fields
  username?: string;
  password?: string;
  url?: string;
  /**
   * Replaces `url` for new items. When set, this is the authoritative list of
   * web origins and native-app identifiers the item matches. `url` is kept
   * for backward compatibility with items written before this field landed.
   */
  identifiers?: Identifier[];
  totp?: string;
  /**
   * User-defined extra fields, e.g. an AWS account ID. Optional and ignored by
   * older clients; surfaced on login items in the editor/detail UI and
   * autofilled by the extension via name-based heuristic matching.
   */
  customFields?: CustomField[];
  /**
   * Up to four previously-used passwords, **newest first**. The current password
   * stays in `password`; on a change the outgoing password is prepended here with
   * `replacedAt = now` and the list is truncated to four. Optional and ignored by
   * older clients, but — like `customFields` — every client MUST preserve it on
   * save (it lives in the encrypted item JSON), or a decrypt→edit→encrypt cycle
   * on a client that lacks the field drops the history. See §3.4 of the client
   * contract. Surfaced via a "show previous passwords" affordance; the current
   * item flows are unchanged.
   */
  passwordHistory?: PasswordHistoryEntry[];
  // Note fields
  content?: string;

  // ── Card fields ──────────────────────────────────────────────────────────
  // Populated only when type === 'card' (payment card). Like customFields,
  // every client MUST preserve these on save — they live INSIDE the encrypted
  // item JSON (no new crypto, no envelope change). Cards are personal-only in
  // v1 (never shared into a family). See docs/superpowers/specs/
  // 2026-10-11-credit-card-storage-and-autofill-design.md.
  /** Name as printed on the card (autocomplete `cc-name`). */
  cardholderName?: string;
  /** Primary account number, **digits only** (no spaces/separators). Formatted
   *  for display/fill via shared/src/card.ts; masked in detail views. */
  cardNumber?: string;
  /** Derived card network (see {@link CardBrand}). Stored (derived on save) so
   *  every client can show brand + last-4 without its own IIN table. */
  cardBrand?: CardBrand;
  /** Expiry month, two digits `"01"`–`"12"`. */
  expMonth?: string;
  /** Expiry year, four digits `"YYYY"`. */
  expYear?: string;
  /** Card security code (CVV/CVC). **Optional** — never required to save a card.
   *  Masked and reveal-gated in the UI, and **never** autofilled or included in
   *  the extension's card match payload. */
  cardSecurityCode?: string;
  /** Optional billing postal code (for AVS). A full billing address is out of
   *  scope — that is the `identity` type's domain. */
  cardBillingZip?: string;

  // ── Binder fields ────────────────────────────────────────────────────────
  // Optional; only set on Binder items (type contact/document/schedule). Every
  // client MUST preserve these on save even without UI (spec §5.1), like
  // customFields. All live INSIDE the encrypted item JSON — the server never
  // sees them, including the item→category assignment below.
  /** Owning Binder category id. Encrypted; server never sees an item's category
   *  (visibility is enforced by the plaintext `circleId`, not by category). */
  categoryId?: string;
  // contact (type === 'contact')
  role?: string;                    // relationship/label: "Plumber", "Pediatrician"
  phones?: ContactField[];
  emails?: ContactField[];
  addresses?: ContactField[];
  company?: string;
  website?: string;
  notes?: string;                   // free notes on a contact (distinct from note `content`)
  avatar?: AttachmentRef;           // optional single contact image
  // document (type === 'document')
  body?: string;                    // rich-ish document text
  links?: DocumentLink[];
  attachments?: AttachmentRef[];    // document photos; manifest inline (spec §6)
  // contact + document: links to related items (documents, sites, contacts…)
  relatedItemIds?: string[];
  // schedule (type === 'schedule')
  rrule?: string;                   // iCalendar RFC 5545 recurrence
  dtstart?: string;                 // ISO-8601 anchor date
  relatedItemId?: string;           // optional single link, e.g. the furnace document
  lastCompletedAt?: string;         // mark-done tracking; next due = rrule + this
  // list (type === 'list') — collaborative family checklist. Entries merge
  // per-id across concurrent edits (listMerge.ts); never whole-item LWW.
  listItems?: ListEntry[];
  // Passkey fields — populated only when type === 'passkey'.
  // Stored encrypted at rest as part of the VaultItem JSON ciphertext.
  // See docs/superpowers/specs/2026-06-10-extension-passkey-provider-design.md.
  rpId?: string;             // e.g. 'github.com'
  rpName?: string;           // e.g. 'GitHub'
  userHandle?: string;       // base64url
  userName?: string;         // e.g. 'jane@example.com'
  userDisplayName?: string;  // human label
  credentialId?: string;     // base64url of 32 random bytes
  publicKeyJwk?: JsonWebKey;
  privateKeyJwk?: JsonWebKey;
  algorithm?: -7;            // ES256, COSE alg id (v1 only)
  lastUsedAt?: string;       // ISO 8601 — updated on each successful assertion
  /**
   * Passkeys associated with this item. Populated when type === 'login' and
   * the user has registered one or more passkeys for the site. New passkey
   * creations always land here (never mint a standalone type: 'passkey'
   * item). Legacy standalone passkey items continue to use the singular
   * passkey fields above and remain readable; no migration runs.
   */
  passkeys?: Passkey[];
  // Sensitivity — when true, filling/copying the password fires a usage event
  // to the org admin
  sensitive: boolean;
  // Org ownership — null/undefined = personal item, set = shared with org
  orgId?: string;
  /** Circle scope for a shared item. Absent = whole family. Plaintext
   *  server metadata threaded back after decryption — NOT part of the ciphertext.
   *  Only set when orgId is set. */
  circleId?: string;
  // Metadata
  favorite: boolean;
  /** Group name — freeform string, e.g. "Work", "Finance". Derived set used for filtering. */
  group?: string;
  tags: string[];
  /**
   * Where this item came from. Set by import adapters; undefined for items
   * created manually in the safe. Surfaced in autofill suggestion lists when
   * a site has multiple matches so the user can tell duplicates apart.
   */
  importSource?: ImportSource;
  /**
   * Set to true once the post-fill "delete the duplicate?" prompt has been
   * shown for any item in this duplicate group. Prevents re-asking when the
   * user has already chosen to keep both.
   */
  dedupePromptShown?: boolean;
  createdAt: string; // ISO 8601
  updatedAt: string; // ISO 8601
}

/** Adapter identifiers used as VaultItem.importSource. Extend when adding new import adapters. */
export type ImportSource = 'lastpass' | 'google' | 'firefox' | 'spreadsheet';

/**
 * Payment-card network, derived from the card number's IIN range by
 * {@link detectCardBrand} (shared/src/card.ts) and stored on `VaultItem.cardBrand`.
 * `'unknown'` covers an empty/unrecognized number.
 */
export type CardBrand =
  | 'visa'
  | 'mastercard'
  | 'amex'
  | 'discover'
  | 'diners'
  | 'jcb'
  | 'unionpay'
  | 'maestro'
  | 'unknown';

/** Human-readable name for each ImportSource, surfaced in the autofill UI. */
export const IMPORT_SOURCE_LABELS: Record<ImportSource, string> = {
  lastpass: 'LastPass',
  google: 'Google Password Manager',
  firefox: 'Firefox Password Manager',
  spreadsheet: 'Spreadsheet CSV',
};

/** Encrypted vault item — safe to persist in IndexedDB and send to server */
export interface EncryptedVaultItem {
  id: string;
  /** AES-256-GCM encrypted JSON of VaultItem, base64-encoded */
  ciphertext: string;
  /** Base64-encoded 12-byte IV used for this item's encryption */
  iv: string;
  /** Client-side timestamp of last modification (ISO 8601) */
  clientUpdatedAt: string;
  /** Server-assigned timestamp — used for sync conflict resolution */
  serverUpdatedAt?: string;
  /** Mirrors VaultItem.orgId so the server can route/filter without decrypting */
  orgId?: string;
  /** Plaintext item type, threaded onto push envelopes so the server can
   *  persist it (create) and gate member edits against the STORED value.
   *  Never the gate input for an item that already has a stored type. */
  type?: VaultItemType;
  /** Phase A: only set on shared items (orgId present). ECIES ephemeral
   *  pubkey paired with the family's static pubkey to derive the AES key
   *  for ciphertext. */
  ephemeralPublicKey?: string;
  /** Circle this shared item is scoped to. Plaintext server metadata — NOT part
   *  of the ciphertext; a visibility hint only. On a **push**, three states:
   *  a string sets the scope; **`null` explicitly clears it** to whole-family;
   *  **absent/omitted preserves** the server's stored value (so a circle-unaware
   *  or stale client can't silently widen a scoped item). On a **pull** it is a
   *  string or absent — never null. */
  circleId?: string | null;
}

// ─── Organisation / Family Types ─────────────────────────────────────────────

export type OrgRole = 'admin' | 'member';

export interface Organization {
  id: string;
  name: string;
  adminUserId: string;
  publicKey: string;           // base64url(32 bytes), X25519 family public key
  createdAt: string;
  // PKA (Phase 2): the founder's Ed25519 signature over `publicKey`, so members
  // can authenticate the family key against the founder's identity before
  // trusting it (defeats a malicious server substituting the family key).
  publicKeySig?: SignedKey;
  // The founder's (adminUserId's) identity public key, served alongside so a
  // joining/sharing client can verify `publicKeySig` without a getOrg round-trip.
  adminIdentityPublicKey?: string;  // base64url(32 bytes) Ed25519
}

export interface OrgMember {
  userId: string;
  email: string;
  role: OrgRole;
  joinedAt: string;
  // Phase 3: published so promoting admins can encrypt the handoff bundle
  accountPublicKey?: string;
  // PKA: the member's Ed25519 identity key and the self-signature over their
  // account key, so a peer (e.g. someone designating them as a recovery contact)
  // can authenticate accountPublicKey before wrapping a secret to it.
  identityPublicKey?: string;
  accountPublicKeySig?: SignedKey;
  familyKeyWrap?: FamilyKeyWrap;
  /** Circles (within this org) this member belongs to — for the management UI. */
  circleIds?: string[];
  /** The member's self-chosen display name, ECIES-wrapped to the FAMILY key
   *  using `share-item-v1` — the same scheme and the same three fields as a
   *  Binder category's name. Plaintext is `{ name: string }`. Absent means the
   *  member has not set one; clients fall back to `email`.
   *
   *  The server never sees the name. It also cannot tell who wrote it: the blob
   *  is encrypted TO the family key, not signed by an individual, so any member
   *  could mint a structurally valid blob for any row. "Self-chosen" is the
   *  authorization rule in `handleUpdateMemberName`, not a crypto property. */
  encryptedName?: string;
  encryptedNameIv?: string;
  /** The ECIES ephemeral public key for `encryptedName`. Distinct from any
   *  other `ephemeralPublicKey` on this type — it belongs to this blob only. */
  encryptedNameEphemeralPublicKey?: string;
}

export interface OrgInvite {
  token: string;
  orgId: string;
  orgName: string;
  invitedEmail: string;
  invitedByUserId: string;
  expiresAt: string;
  createdAt: string;
}

// ─── Usage Event Types ────────────────────────────────────────────────────────

export type UsageEventAction = 'autofill' | 'copy' | 'totp_autofill' | 'totp_copy';

/**
 * Fired when a user accesses a sensitive vault item's password.
 * Written to DynamoDB immediately; will also dispatch a push notification
 * to the org admin when mobile support is added.
 */
export interface UsageEvent {
  id: string;
  orgId: string;
  itemId: string;
  userId: string;
  userEmail: string;
  action: UsageEventAction;
  occurredAt: string;
  encryptedPayload: string;
  encryptedPayloadIv: string;
  ephemeralPublicKey: string;
  /** Phase 4: client platform that produced the event. One of KNOWN_PLATFORMS values. */
  platform?: string;
}

// ─── Org API Request / Response Types ────────────────────────────────────────

/** ECIES bundle: the family private key encrypted to a member's account
 *  public key. Exactly the shape of encryptFamilyHandoff()'s return value.
 *  Stored as one atomic attribute — never as three loose fields. */
export interface FamilyKeyWrap {
  encryptedFamilyKey: string;     // base64 — ECIES ciphertext + 16-byte tag
  encryptedFamilyKeyIv: string;   // base64 — 12-byte AES-GCM IV
  ephemeralPublicKey: string;     // base64url — 32-byte X25519 ephemeral public key
}

export interface CreateOrgRequest {
  name: string;
  publicKey: string;            // base64url(32 bytes), family X25519 public key
  familyKeyWrap: FamilyKeyWrap; // family privkey wrapped to the creator's account pubkey
  publicKeySig?: SignedKey;     // PKA: founder's signature over publicKey. Optional in the
                                // wire type, but clients always send it now (creation signs;
                                // an unsigned family fails authentication on every client).
}

export interface CreateOrgResponse {
  org: Organization;
}

export interface InviteMemberRequest {
  email: string;
  /**
   * ECIES wrap of the family privkey to the invitee's account pubkey.
   * Present for existing-user invites; ABSENT for email-invites (non-HBS
   * recipient). Server uses absence as the discriminator.
   */
  familyKeyWrap?: FamilyKeyWrap;
}

export interface InviteMemberResponse {
  /** Discriminator: 'regular' = invitee was already an HBS user;
   *  'email-invite' = invitee did not exist, we sent them an email. */
  kind: 'regular' | 'email-invite';
  invite: OrgInvite | OrgEmailInviteSummary;
}

export interface OrgEmailInviteSummary {
  token: string;
  orgId: string;
  orgName: string;
  invitedEmail: string;
  invitedByUserId: string;
  expiresAt: string;
  createdAt: string;
  state: 'awaiting-registration' | 'awaiting-finalization';
}

export interface FinalizeEmailInviteRequest {
  familyKeyWrap: FamilyKeyWrap;
  inviteeUserId: string;
}

export interface FinalizeEmailInviteResponse {
  ok: true;
  invite: OrgInvite;
}

export interface AcceptInviteRequest {
  token: string;
}

export interface InviteeKeyResponse {
  userId: string;
  accountPublicKey: string;
  // PKA: so the admin can authenticate the invitee's account key (against their
  // identity key) before wrapping the family private key to it (site A1).
  identityPublicKey?: string;
  accountPublicKeySig?: SignedKey;
}

export interface OrgCoverage {
  /** Whether the family currently has active backing coverage. */
  covered: boolean;
  reason: 'linked-active' | 'owner-trial' | 'sub-lapsed' | 'no-sub';
  /** ISO timestamp of the last coverage recompute. */
  computedAt: string;
}

export interface ListOrgsResponse {
  orgs: Array<Organization & {
    role: OrgRole;
    familyKeyWrap?: FamilyKeyWrap;   // present on the caller's own membership
    /**
     * Stripe subscription status of THIS family's backing subscription
     * (or `undefined` when no sub is linked). Used by clients to identify
     * a lapsed family on a per-org basis (the singular activeFamilyId /
     * activeSubscriptionId fields were removed from EntitlementSnapshot in
     * Phase 2 T14 — they did not compose under multi-family).
     * Values mirror Stripe's verbatim status strings: 'active', 'trialing',
     * 'past_due', 'canceled', 'unpaid', 'incomplete', etc.
     */
    backingSubStatus?: string;
    /**
     * Cached per-family coverage (computed off the hot path; see 02-server
     * §recompute). Absent = pre-backfill → clients treat as covered (fail-open).
     * Authoritative for showing/hiding a family; supersedes backingSubStatus.
     */
    covered?: boolean;
    coverageReason?: OrgCoverage['reason'];
  }>;
}

export interface ListMembersResponse {
  members: OrgMember[];
}

// ─── Pending-invite API types (UX fixes sub-project) ─────────────────────────

export interface PendingInvite {
  orgId: string;
  orgName: string;
  token: string;
  invitedByUserId: string;
  expiresAt: string;
  createdAt: string;
  /**
   * 'regular' = existing INVITE# row (Accept/Decline both work).
   * 'email-invite' = EMAIL_INVITE# row in state=awaiting-finalization
   * (Decline only; Accept becomes available after admin Finalize).
   */
  kind: 'regular' | 'email-invite';
  /** Set only when kind='email-invite'. */
  emailInviteState?: 'awaiting-finalization';
}

export interface ListPendingInvitesResponse {
  invites: PendingInvite[];
}

// ─── Outstanding-invite (admin view) API types ───────────────────────────────

/**
 * One outstanding invite as seen by the admin of the inviting family.
 * Distinct from PendingInvite which is the invitee's view (no email field
 * because the invitee already knows it's theirs).
 */
export interface OutstandingOrgInvite {
  token: string;
  invitedEmail: string;
  invitedByUserId: string;
  createdAt: string;
  expiresAt: string;
}

export interface ListOrgInvitesResponse {
  invites: OutstandingOrgInvite[];
  emailInvites: OutstandingOrgEmailInvite[];
}

export interface OutstandingOrgEmailInvite {
  token: string;
  invitedEmail: string;
  invitedByUserId: string;
  createdAt: string;
  expiresAt: string;
  state: 'awaiting-registration' | 'awaiting-finalization';
}

// ─── Usage Event API Types ────────────────────────────────────────────────────

export interface LogUsageEventRequest {
  orgId: string;
  itemId: string;
  action: UsageEventAction;
  encryptedPayload: string;        // base64, AES-GCM ciphertext + 16-byte auth tag
  encryptedPayloadIv: string;      // base64, 12-byte IV
  ephemeralPublicKey: string;      // base64url, 32-byte X25519 ephemeral pubkey
}

export interface ListUsageEventsResponse {
  events: UsageEvent[];
  nextToken?: string; // for pagination
}

// ─── Account Key API Types (Phase 3) ─────────────────────────────────────────

export interface UploadAccountKeyRequest {
  accountPublicKey: string;             // base64url, 43 chars (32 bytes)
  wrappedAccountPrivateKey: string;     // base64, AES-GCM ciphertext + 16-byte auth tag
  wrappedAccountPrivateKeyIv: string;   // base64, 12-byte IV
}

export interface GetAccountKeyResponse {
  accountPublicKey: string;             // base64url, 43 chars (32 bytes)
  wrappedAccountPrivateKey: string;     // base64, AES-GCM ciphertext + 16-byte auth tag
  wrappedAccountPrivateKeyIv: string;   // base64, 12-byte IV
}

// ─── Identity Key API Types (PKA — Public Key Authentication) ─────────────────
// The per-user Ed25519 identity key signs the encryption public keys the user
// publishes so a malicious server can't substitute one. Stored twice-wrapped,
// exactly like the account key. `accountPublicKeySig` (the self-signature over
// the account public key) is uploaded alongside so both land in one write.

export interface UploadIdentityKeyRequest {
  identityPublicKey: string;              // base64url, 43 chars (Ed25519, 32 bytes)
  wrappedIdentityPrivateKey: string;      // base64, AES-GCM ciphertext + 16-byte auth tag
  wrappedIdentityPrivateKeyIv: string;    // base64, 12-byte IV
  accountPublicKeySig?: SignedKey;        // self-signature over the account public key
}

export interface GetIdentityKeyResponse {
  identityPublicKey: string;
  wrappedIdentityPrivateKey: string;
  wrappedIdentityPrivateKeyIv: string;
}

// ─── Sync Types ───────────────────────────────────────────────────────────────

export interface SyncPushRequest {
  items: EncryptedVaultItem[];
  deletedIds: string[];
}

export interface SyncPushResponse {
  syncedAt: string;
  conflicts: Array<{
    id: string;
    serverItem: EncryptedVaultItem;
  }>;
  /** Count of items in this push that didn't previously exist server-side. */
  itemsStored?: number;
  /**
   * Ids of items whose write the server rejected because the caller isn't
   * authorized (e.g., a non-admin family member trying to edit a shared
   * item). The server still processes the rest of the batch — the client
   * should `console.warn` these and avoid resending them. Empty when no
   * items were rejected.
   */
  rejectedItemIds: string[];
  /**
   * Same as rejectedItemIds but for deletedIds the server refused to
   * tombstone (non-admin attempting to delete a shared item). Empty when
   * no deletes were rejected.
   */
  rejectedDeletedIds: string[];
}

export interface SyncPullResponse {
  items: EncryptedVaultItem[];
  deletedIds: string[];
  serverTime: string;
}

// ─── Auth Types ───────────────────────────────────────────────────────────────

export interface AuthResponse {
  userId: string;
  accessToken: string;
  idToken: string;       // Cognito ID token — used for API Gateway JWT auth (has aud claim)
  refreshToken: string;
  expiresIn: number;
}

export interface RefreshRequest {
  refreshToken: string;
}

export interface LogoutRequest {
  /** Refresh token of the calling device. Server revokes only this token
   *  via Cognito RevokeToken. Absent = no server-side revocation. */
  refreshToken?: string;
}

// ─── OPAQUE Auth Types ────────────────────────────────────────────────────────

export interface OpaqueRegisterStartRequest {
  email: string;
  registrationRequest: string;
}

export interface OpaqueRegisterStartResponse {
  registrationResponse: string;
}

export interface OpaqueRegisterFinishRequest {
  email: string;
  registrationRecord: string;
  encryptedVaultKey: string;
  encryptedVaultKeyIv: string;
  /**
   * Optional, self-declared marketing attribution slug — where the user heard
   * about us (e.g. 'ad-display', 'search') or a campaign tag carried from a
   * landing page's `?src=`. Charset-bounded server-side; never PII. Stored as
   * metadata only; never gates auth.
   */
  signupSource?: string;
  /**
   * The user's account-level X25519 keypair, published ATOMICALLY with account
   * creation so it can never be missing. The public key is stored plaintext on
   * the PROFILE row; the private key is wrapped under
   * `HKDF(exportKey, "account-key-wrapping")` (same scheme as
   * `PUT /users/me/account-key`). All three are sent together or not at all.
   *
   * Optional for backward compatibility: older clients omit them and fall back
   * to the lazy `PUT /users/me/account-key` bootstrap. Sending them here closes
   * the window where a signup completes but the deferred publish is dropped
   * (tab backgrounded mid-publish, app suspended, transient error) — leaving a
   * user un-invitable to families (peers get NOT_A_USER).
   */
  accountPublicKey?: string;              // base64url, 43 chars (32 bytes)
  wrappedAccountPrivateKey?: string;      // base64 of AES-GCM ciphertext + tag
  wrappedAccountPrivateKeyIv?: string;    // base64 of 12-byte IV
}

// OpaqueRegisterFinishResponse = AuthResponse

export interface OpaqueLoginStartRequest {
  email: string;
  startLoginRequest: string;
}

export interface OpaqueLoginStartResponse {
  loginResponse: string;
  sessionId: string;
}

export interface OpaqueLoginFinishRequest {
  sessionId: string;
  finishLoginRequest: string;
}

export interface OpaqueLoginFinishResponse {
  userId: string;
  accessToken: string;
  idToken: string;       // Cognito ID token — used for API Gateway JWT auth
  refreshToken: string;
  expiresIn: number;
  encryptedVaultKey: string;
  encryptedVaultKeyIv: string;
}

// ─── Password Rotation Types ──────────────────────────────────────────────────

export interface PasswordRotateStartRequest {
  /** OPAQUE login-start request built from the user's CURRENT password. */
  startLoginRequest: string;
  /** OPAQUE registration-start request built from the user's NEW password. */
  registrationRequest: string;
}

export interface PasswordRotateStartResponse {
  /** Short-lived id correlating start → finish; carries the server login state. */
  rotationId: string;
  loginResponse: string;
  registrationResponse: string;
}

export interface PasswordRotateFinishRequest {
  rotationId: string;
  /** OPAQUE login-finish request — proves knowledge of the current password. */
  finishLoginRequest: string;
  /** OPAQUE registration record for the new password. */
  registrationRecord: string;
  /** safeKey re-wrapped under HKDF(newExportKey, "vault-key-wrapping"). */
  newEncryptedVaultKey: string;
  newEncryptedVaultKeyIv: string;
  /**
   * Account privkey re-wrapped under HKDF(newExportKey, "account-key-wrapping").
   * Required: the wrap is keyed off exportKey, which rotates with every
   * OPAQUE registration. Without re-wrapping here, the next login derives a
   * new wrapping key that cannot decrypt the stale on-server blob, the
   * account-key unwrap throws OperationError, and all family-shared items
   * become unreadable. Mirrors the recovery-reset flow at
   * `/auth/recovery/finish`, which rotates this field for the same reason.
   */
  newWrappedAccountPrivateKey: string;
  newWrappedAccountPrivateKeyIv: string;
  /**
   * PKA: Ed25519 identity privkey re-wrapped under HKDF(newExportKey,
   * "identity-signing-key-wrapping"). Same rationale as the account key — the
   * wrap is keyed off exportKey, which rotates. Optional so pre-PKA clients (and
   * users without an identity key yet) can still rotate; when present the server
   * overwrites the stored wrap (the identity PUBLIC key is unchanged).
   */
  newWrappedIdentityPrivateKey?: string;
  newWrappedIdentityPrivateKeyIv?: string;
  /**
   * Fresh PBKDF2 salt for offline-unlock material. Rotated for parity with the
   * recovery-reset flow so the salt lifecycle matches across both reset paths.
   */
  newLocalKdfSalt: string;
}

export interface PasswordRotateFinishResponse {
  ok: true;
}

// ─── Email Verification Types ─────────────────────────────────────────────────

export interface VerifyEmailRequest {
  token: string;
}

export interface VerifyEmailResponse {
  ok: true;
}

// ─── API Response Wrapper ─────────────────────────────────────────────────────

export interface ApiSuccess<T> {
  ok: true;
  data: T;
}

export interface ApiError {
  ok: false;
  error: string;
  code: string;
}

export type ApiResponse<T> = ApiSuccess<T> | ApiError;

// ─── Extension Message Types (background ↔ popup ↔ content) ──────────────────

export type MessageType =
  | 'UNLOCK_VAULT'
  | 'LOCK_VAULT'
  | 'GET_VAULT_STATUS'
  | 'GET_MATCHING_CREDENTIALS'
  | 'AUTOFILL_CREDENTIALS'
  | 'SYNC_VAULT'
  | 'LOG_SENSITIVE_USAGE';

export interface ExtensionMessage {
  type: MessageType;
  payload?: unknown;
}

export interface VaultStatusResponse {
  locked: boolean;
  itemCount: number;
  lastSyncedAt?: string;
}

export interface MatchingCredentialsRequest {
  url: string;
}

export interface MatchingCredentialsResponse {
  matches: Array<{
    id: string;
    name: string;
    username: string;
    url: string;
    sensitive: boolean;
    orgId?: string;
  }>;
}

// ─── DynamoDB Record Shapes (used by Lambda functions) ────────────────────────

/**
 * One device-token registration. iOS entries include `environment` to
 * route the right SNS Platform Application (sandbox in DEBUG builds,
 * production in Release archives). Android entries omit it.
 */
export interface FcmTokenEntry {
  endpointArn: string;
  platform: 'android' | 'ios';
  environment?: 'sandbox' | 'production'; // iOS only; absent on Android
  registeredAt: string;       // ISO 8601
  lastSeenAt: string;         // ISO 8601, bumped on each register call
}

export interface UserRecord {
  pk: string;          // USER#<userId>
  sk: string;          // PROFILE
  userId: string;
  email: string;
  opaqueRecord: string;
  encryptedVaultKey: string;
  encryptedVaultKeyIv: string;
  createdAt: string;
  updatedAt: string;
  /** Distinct client platforms this user has authenticated from. Used
   *  by observability to fire a one-time FirstPlatformUse event per
   *  (user, platform). Stored as a DynamoDB String Set; absent means none yet. */
  seenPlatforms?: string[];
  // Phase 3: account-level X25519 keypair (optional during migration window)
  accountPublicKey?: string;              // base64url, 43 chars (32 bytes)
  wrappedAccountPrivateKey?: string;      // base64 of AES-GCM ciphertext + 16-byte tag
  wrappedAccountPrivateKeyIv?: string;    // base64 of 12-byte IV
  // PKA: per-user Ed25519 identity key (optional during migration window) and
  // the self-signature over accountPublicKey. Served to peers (via the org
  // roster) so they can authenticate this user's account key before wrapping.
  identityPublicKey?: string;             // base64url, 43 chars (Ed25519, 32 bytes)
  wrappedIdentityPrivateKey?: string;     // base64 of AES-GCM ciphertext + 16-byte tag
  wrappedIdentityPrivateKeyIv?: string;   // base64 of 12-byte IV
  accountPublicKeySig?: SignedKey;        // Ed25519 self-signature over accountPublicKey
  /** True once the user has confirmed their email via the verification link. */
  emailVerified?: boolean;
  /** ISO timestamp of when the email was verified. */
  emailVerifiedAt?: string;
  // Sub-spec #2 additions — populated lazily; absent = pre-rollout.
  trialStartedAt?: string;
  entitlement?: EntitlementSnapshot;
  pickedSurface?: 'extension' | 'mobile' | 'ios' | 'android';
  pickedSurfaceChangedAt?: string;
  /**
   * Optional, self-declared marketing-attribution slug captured at registration
   * ("how did you hear about us?" / a campaign `?src=` tag). Charset-bounded,
   * never PII. Absent for users who skipped it or registered before the field
   * existed. Attribution metadata only.
   */
  signupSource?: string;
  /**
   * FCM device tokens registered by this user's Android clients. Keyed
   * by the FCM token string. Created/updated by POST /users/me/notification-tokens
   * and removed by DELETE …/{token} or by the SNS EndpointDisabled cleanup
   * path in sendPush().
   */
  fcmTokens?: Record<string, FcmTokenEntry>;
}

export interface VaultItemRecord {
  pk: string;          // USER#<userId>  (personal) | ORG#<orgId> (shared)
  sk: string;          // ITEM#<itemId>
  userId: string;      // owner (creator) of the item
  itemId: string;
  ciphertext: string;
  iv: string;
  clientUpdatedAt: string;
  serverUpdatedAt: string;
  deleted: boolean;
  orgId?: string;
  /** Plaintext item type, persisted on first create in an ORG# partition and
   *  immutable on member updates. The server's member-edit gate reads this,
   *  never the updater's envelope. Absent on pre-feature records → admin-only. */
  type?: VaultItemType;
  /** Phase A: only set on shared items. */
  ephemeralPublicKey?: string;
  /** Circle this shared item is scoped to. Absent = whole family. Plaintext
   *  server metadata — NOT part of the ciphertext; a visibility hint only. */
  circleId?: string;
}

export interface OrgRecord {
  pk: string;          // ORG#<orgId>
  sk: string;          // PROFILE
  orgId: string;
  name: string;
  adminUserId: string;
  publicKey: string;   // base64url(32 bytes)
  publicKeySig?: SignedKey; // PKA: founder's signature over publicKey (Phase 2)
  createdAt: string;
  updatedAt: string;
}

export interface OrgMemberRecord {
  pk: string;          // ORG#<orgId>
  sk: string;          // MEMBER#<userId>
  orgId: string;
  userId: string;
  email: string;
  role: OrgRole;
  joinedAt: string;
  /** Required. The family private key, ECIES-wrapped to this member's account
   *  public key. Set at family-create (wrapped to the creator) and copied
   *  verbatim from the invite at accept-time — every member row always has it. */
  familyKeyWrap: FamilyKeyWrap;
  gsi1pk: string;      // USER#<userId>
  gsi1sk: string;      // ORG#<orgId>
  /** Circles (within this org) the member belongs to. Absent/empty = none.
   *  The vault read uses this to filter shared items by circle. */
  circleIds?: string[];
  /** Self-chosen display name, ECIES-wrapped to the family key. See `OrgMember`.
   *  Stored under `encryptedName` rather than `name` because `name` is a
   *  DynamoDB reserved word. */
  encryptedName?: string;
  encryptedNameIv?: string;
  encryptedNameEphemeralPublicKey?: string;
}

export interface CircleRecord {
  pk: string;          // ORG#<orgId>
  sk: string;          // CIRCLE#<circleId>
  circleId: string;
  orgId: string;
  name: string;
  createdAt: string;
  createdBy: string;   // userId of the admin who created it
}

/** Circle summary for the management UI. */
export interface Circle {
  id: string;
  name: string;
  memberCount: number;
}

export interface CreateCircleRequest { name: string }
export interface RenameCircleRequest { name: string }
export interface ListCirclesResponse { circles: Circle[] }

// ─── Binder categories ────────────────────────────────────────────────────────
// A Category is the Binder's topic axis (Home, Finances, Health…), a first-class
// record in the ORG# partition paralleling CIRCLE#. Visibility rides the
// category's `circleId` (absent = whole family); items inherit it. The name/icon
// are ECIES-wrapped to the family public key, so — unlike Circle names — the
// server never sees the topic label. See spec §5.2/§7.

export interface CategoryRecord {
  pk: string;          // ORG#<orgId>
  sk: string;          // CATEGORY#<categoryId>
  categoryId: string;
  orgId: string;
  /** Visibility scope; absent = whole family. Drives which members the server
   *  returns this category (and its items, via the denormalized item circleId) to. */
  circleId?: string;
  /** Sidebar sort order. */
  order: number;
  /** Category name (+ optional icon) ECIES-wrapped to the family public key,
   *  same share-item-v1-shaped scheme as shared items. Opaque to the server. */
  encryptedName: string;
  encryptedNameIv: string;
  ephemeralPublicKey: string;
  createdAt: string;
  createdBy: string;   // userId of the admin who created it
  updatedAt: string;
}

/** Decrypted category summary for the client sidebar. */
export interface Category {
  id: string;
  name: string;
  icon?: string;
  circleId?: string;
  order: number;
}

/** Wire shape of a category as the server returns it — still encrypted; the
 *  client decrypts `encryptedName` into a `Category`. */
export interface CategorySummary {
  categoryId: string;
  circleId?: string;
  order: number;
  encryptedName: string;
  encryptedNameIv: string;
  ephemeralPublicKey: string;
}

export interface CreateCategoryRequest {
  encryptedName: string;
  encryptedNameIv: string;
  ephemeralPublicKey: string;
  circleId?: string;
  order: number;
}

export interface UpdateCategoryRequest {
  encryptedName?: string;
  encryptedNameIv?: string;
  ephemeralPublicKey?: string;
  /** `null` clears the scope (→ whole family); omitted leaves it unchanged. */
  circleId?: string | null;
  order?: number;
}

/** Body of `PATCH /orgs/{orgId}/members/{userId}/name`. All three are required
 *  together — a partial triple cannot be decrypted. Clearing a name uses
 *  `DELETE` on the same path, not an empty PATCH. */
export interface UpdateMemberNameRequest {
  encryptedName: string;
  encryptedNameIv: string;
  ephemeralPublicKey: string;
}

export interface ListCategoriesResponse { categories: CategorySummary[] }

// ─── Binder attachments (encrypted image blobs in S3) ─────────────────────────
// Presigned-URL endpoints: the Lambda authorizes family (+circle) membership and
// returns a short-lived S3 URL; the client uploads/downloads ciphertext directly
// and the Lambda never touches the bytes. See spec §6/§8.

export interface CreateAttachmentRequest {
  orgId: string;
  itemId: string;
  attachmentId: string;
  mime: string;        // image/* only, day one
  size: number;        // bytes
}

export interface CreateAttachmentResponse {
  s3Key: string;       // "org/<orgId>/<itemId>/<attachmentId>"
  uploadUrl: string;   // presigned PUT — client uploads ciphertext directly to S3
  expiresIn: number;   // seconds
}

export interface GetAttachmentResponse {
  downloadUrl: string; // presigned GET — client downloads ciphertext, decrypts locally
  expiresIn: number;   // seconds
}

export interface OrgInviteRecord {
  pk: string;          // ORG#<orgId>
  sk: string;          // INVITE#<token>
  orgId: string;
  orgName: string;
  token: string;
  invitedEmail: string;
  invitedByUserId: string;
  expiresAt: string;
  createdAt: string;
  ttl: number;         // DynamoDB auto-expiry (Unix seconds)
  /** UX-fixes sub-project: GSI-3 keys for querying invites by invited email. Optional for back-compat with records written before this sub-project landed. */
  gsi3pk?: string;  // "EMAIL#<lowercase email>"
  gsi3sk?: string;  // "INVITE#<token>"
  /** Required. An invite can only be created for an email that is already a
   *  registered Hey Be Safe user; the admin's client wraps the family key to
   *  the invitee's account public key before the invite is stored. */
  familyKeyWrap: FamilyKeyWrap;
}

export interface UsageEventRecord {
  pk: string;
  sk: string;
  orgId: string;
  eventId: string;
  itemId: string;
  userId: string;
  userEmail: string;
  action: UsageEventAction;
  occurredAt: string;
  encryptedPayload: string;
  encryptedPayloadIv: string;
  ephemeralPublicKey: string;
  /** Phase 4: client platform. Optional for pre-Phase-4 records. */
  platform?: string;
  gsi2pk: string;
  gsi2sk: string;
}

// ─── Encoding helpers ─────────────────────────────────────────────────────────
export * from './encoding';

// ─── Import Adapters ──────────────────────────────────────────────────────────
export type { ImportAdapter, ImportedItem, CaptureProps } from './importers/index';
export { LastPassAdapter, GoogleAdapter, FirefoxAdapter, PhotoAdapter, SpreadsheetAdapter } from './importers/index';
export { autoDetectMapping, applyMapping, parseCsv, fillMissingUrlsFromNames } from './importers/index';
export { detectImportAdapter, csvHeaderSet } from './importers/index';
export type { VaultField, Mapping } from './importers/index';

// ─── Brand → domain inference (on-device; pre-fills a site's primary URL) ─────
export { suggestDomainForName, BRAND_DOMAINS } from './brandDomain';

// ─── TOTP ─────────────────────────────────────────────────────────────────────
export { base32Decode, parseTotp, generateCode } from './totp';
export type { TotpAlgorithm, ParsedTotp, GeneratedCode } from './totp';

// ─── Usage-event crypto ────────────────────────────────────────────────────────
export { encryptUsageEventPayload, decryptUsageEventPayload } from './usageEventCrypto';
export type { EncryptedUsagePayload, UsageEventPlaintext } from './usageEventCrypto';

// ─── Family-handoff crypto (Phase 3) ───────────────────────────────────────
export { encryptFamilyHandoff, decryptFamilyHandoff } from './familyHandoffCrypto';
export type { EncryptedHandoff } from './familyHandoffCrypto';
export * from './accountKeyCrypto';
export * from './identityKeyCrypto';
export * from './signedKey';
export * from './safetyWords';
export * from './sharedItemCrypto';
export { wrapAttachmentKey, unwrapAttachmentKey } from './attachmentKeyCrypto';
export type { WrappedContentKey } from './attachmentKeyCrypto';
export * from './recoveryContactCrypto';
export * from './maskEmail';
export { canonicalizeEmail, DOT_EQUIVALENT_PROVIDERS } from './canonicalEmail';
export * from './subscriptions';
export { deriveEntitlementCaps, type EntitlementCaps } from './deriveEntitlementCaps';
export * from './entitlementError';

export {
  ENTITLEMENT_ERROR_MESSAGES,
  GENERIC_ERROR_MESSAGE,
  friendlyEntitlementError,
  entitlementErrorOr,
} from './entitlementErrorMessages';
export * from './apiVersion';
export { isEntitlementErrorResponse } from './isEntitlementErrorResponse';
export { effectiveWebIdentifiers, effectiveAppIdentifiers, effectivePasskeys, urlMatchesRpId } from './identifiers';
export { RELATED_DOMAIN_GROUPS, relatedDomains, areRelatedDomains } from './relatedDomains';
export {
  BASELINE_SITE_RULES, SITE_RULES_FORMAT, SITE_RULES_VERSION,
  equivalentGroupsOf, areRelatedInBundle, siteRuleFor,
  pspAdapterForOrigin, pspFieldRole, pspFieldRoleFromTitle, pspFieldRoleFromName,
  type SiteRulesBundle, type SiteRule, type EquivalentGroup, type SiteQuirk, type SiteForm,
  type PspAdapter, type PspFieldRole, type PspRoleByQueryParam, type PspRoleByFrameTitle,
  type PspRoleByFrameName, type PspRole,
} from './siteRules';
export { SHIPPED_SITE_RULES } from './siteRulesSnapshot';
export { verifySiteRules, siteRulesCanonicalBytes } from './siteRulesSignature';
export {
  parsePasswordRules, constraintsToGeneratorOptions, allowedSymbolSet,
  satisfiesPasswordRules, generatePasswordForRules, PASSWORD_RULE_MAX_ATTEMPTS,
  constraintsFromGeneratorOptions,
  type PasswordRuleConstraints, type CharClass, type NamedCharClass, type GeneratorCharsetOptions,
} from './passwordRules';
export {
  transformPmr,
  type PmrInputs, type PmrOverrides, type PmrCandidate, type PmrTransformResult,
} from './pmrTransform';
export { ensureUrlScheme, tryParseUrl, normalizedHost, normalizeUrlForStorage } from './url';
export { sortItemsForSafeList, DEFAULT_SAFE_LIST_SORT, SAFE_LIST_SORT_ORDERS } from './safeListSort';
export type { SafeListSortOrder } from './safeListSort';
export {
  SUPPORT_CHAT_LIMITS,
  type SupportChatMessage,
  type SupportChatRequest,
  type SupportChatSource,
  type SupportChatResponse,
} from './supportChat';
export {
  EXTENSION_STORE_URLS,
  detectBrowser,
  extensionStoreUrl,
  type ExtensionBrowser,
} from './extensionLinks';
export {
  EXTENSION_PRESENCE_ATTR,
  EXTENSION_PROBE_IDS,
  EXTENSION_PROBE_PATH,
  extensionProbeUrl,
} from './extensionPresence';
export {
  HBS_WEB_MESSAGE_SOURCE,
  HBS_EXT_MESSAGE_SOURCE,
  HBS_SUSPEND_PASSKEY_PROVIDER,
  HBS_PASSKEY_PROVIDER_SUSPENDED,
  HBS_RESUME_PASSKEY_PROVIDER,
  HBS_PASSKEY_PROVIDER_RESUMED,
  type HbsWebHandshakeMessage,
  type HbsExtHandshakeMessage,
} from './passkeyDetachHandshake';
export * from './conditionalPasskeyHandshake';
export { isBinderType } from './vaultItemTypes';
export {
  detectCardBrand, luhnValid, cardLast4, maskedCardNumber, formatCardNumber, formatExpiry,
} from './card';
export type { ExpiryStyle } from './card';
export {
  MIN_PASSWORD_LENGTH,
  WEAK_STRENGTH_THRESHOLD,
  passwordStrength,
  isWeakPassword,
  reusedGroups,
  pwnedPrefix,
  isPwned,
  PWNED_RANGE_BATCH_SIZE,
} from './passwordCheckup';
export type { PwnedRangeBatchRequest, PwnedRangeBatchResponse } from './passwordCheckup';
export {
  FINANCIAL_DOMAINS,
  isFinancialHost,
  isFinancialItem,
} from './financialDomains';
export {
  buildCheckupRows,
  compareCheckupRows,
  type CheckupTier,
  type CheckupLoginInput,
  type CheckupDomainRow,
  type CheckupRowsResult,
} from './passwordCheckupRows';
export { mergeListEntries, mergeListItem } from './listMerge';
export { keyBetween, BASE62 } from './fractionalIndex';
export { compareEntries, displayEntries, ensurePositions, moveEntry } from './listEditing';
export {
  encryptSafeExport, decryptSafeExport, toImportedItems, itemTypeCounts, passkeyCount,
  WrongPassphraseError, MalformedBackupError,
  SAFE_EXPORT_FORMAT, SAFE_EXPORT_VERSION, SAFE_EXPORT_EXTENSION,
  type SafeExportEnvelope,
} from './safeExport';

// ─── Recovery Contact (family-based trust) ────────────────────────────────────

export type RecoveryContactState =
  | 'PENDING_ACCEPT'
  | 'ACTIVE'
  | 'RESET_REQUESTED'
  | 'RESET_APPROVED'
  | 'EMERGENCY_REQUESTED';

export type RecoveryAuditAction =
  | 'designate'
  | 'accept'
  | 'decline'
  | 'remove'
  | 'reset-request'
  | 'reset-approve'
  | 'reset-deny'
  | 'reset-cancel'
  | 'reset-finish'
  | 'reset-expire'
  | 'emergency-request'
  | 'emergency-cancel'
  | 'emergency-granted'
  | 'emergency-items-read';

// ─── Emergency-access wait period ────────────────────────────────────────────
// The waiting window between a contact's `emergency-request` and their first
// successful key fetch. Per-relationship: each row on RecoveryContactRecord
// carries its own `emergencyWaitHours`. Stored as hours (integer) so audit
// logs stay readable and the JSON shape is unambiguous.

/** Picker presets the designate UI offers (hours). 24h, 3 days, 7 days,
 *  14 days, 30 days — picked to span "trusted spouse" to "long-tail contact". */
export const EMERGENCY_WAIT_PRESETS_HOURS = [24, 72, 168, 336, 720] as const;
/** Default + fallback when a row predates the field (or the request omits it).
 *  168 hours = 7 days, preserving the pre-feature behavior. */
export const EMERGENCY_WAIT_DEFAULT_HOURS = 168;
/** Server-enforced floor. Prevents a compromised account from setting "0
 *  minutes" to instantly read its own safe via a colluding contact. */
export const EMERGENCY_WAIT_MIN_HOURS = 24;

export interface RecoveryContactRecord {
  pk: string;          // USER#<protectedUserId>
  sk: string;          // RECOVERY_CONTACT#<contactUserId>
  protectedUserId: string;
  contactUserId: string;
  protectedEmail: string;
  contactEmail: string;
  wrappedVaultKey: string;            // base64(EncryptedBundle JSON)
  wrappedAccountPrivateKey: string;   // base64(EncryptedBundle JSON)
  state: RecoveryContactState;
  createdAt: string;
  acceptedAt?: string;
  lastTransitionAt: string;
  /** Waiting window (hours) between `emergency-request` and the contact's
   *  first successful key fetch. Set at designate-time; immutable for the
   *  life of the relationship. Optional for backward-compat with rows
   *  predating the feature — server falls back to EMERGENCY_WAIT_DEFAULT_HOURS. */
  emergencyWaitHours?: number;
  emergencyInitiatedAt?: string;
  emergencyReleaseAt?: string;
  resetEphemeralPub?: string;             // base64url X25519 pub
  resetWrappedVaultKey?: string;          // base64(EncryptedBundle JSON)
  resetWrappedAccountPrivateKey?: string; // base64(EncryptedBundle JSON)
  resetExpiresAt?: string;
  /** Set alongside resetEphemeralPub when a reset is requested. Bound into the
   *  signed deny token so `/auth/recovery/deny` can confirm the link targets
   *  the still-current request (a stale requestId is a no-op, not a replay). */
  requestId?: string;
  /** Layer 2b contact-verification code, hashed at rest (sha256 salted by
   *  protectedUserId). Set alongside resetEphemeralPub on a reset request; the
   *  contact must type the matching 6-digit code on approve. */
  verificationCodeHash?: string;
  /** Number of failed verification-code attempts on the current request;
   *  attempt-capped so the 6-digit code can't be brute-forced online. */
  verificationAttempts?: number;
  /** ISO timestamp set on the first successful wrapped-vault-key fetch. Prevents
   *  duplicate audit writes and notification emails on contact retries. */
  grantedAt?: string;
  /** ISO timestamp set when the 24h-before-release reminder email was sent.
   *  Prevents firing the reminder more than once. */
  reminderSentAt?: string;
  gsi1pk?: string;     // USER#<contactUserId> (for inbound listing)
  gsi1sk?: string;     // RECOVERY_OF#<protectedUserId>
}

export interface RecoveryAuditRecord {
  pk: string;          // USER#<protectedUserId>
  sk: string;          // RECOVERY_AUDIT#<isoTimestamp>#<random>
  action: RecoveryAuditAction;
  actorUserId: string;
  contactUserId: string;
  timestamp: string;
  /** Optional action-specific context. For `emergency-items-read` this carries
   *  `{ itemCount }` so an auditor can reason about disclosure scope. */
  metadata?: Record<string, unknown>;
}

// ─── Recovery API request/response types ──────────────────────────────────────

export interface DesignateRecoveryContactRequest {
  contactUserId: string;
  wrappedVaultKey: string;
  wrappedAccountPrivateKey: string;
  /** Hours the protected user has to cancel an emergency request before
   *  the contact gets access. Server validates >= EMERGENCY_WAIT_MIN_HOURS
   *  and falls back to EMERGENCY_WAIT_DEFAULT_HOURS when omitted. */
  emergencyWaitHours?: number;
}

export interface RecoveryContactSummary {
  contactUserId: string;
  contactEmail: string;
  state: RecoveryContactState;
  createdAt: string;
  acceptedAt?: string;
  /** Per-relationship wait period (hours). Surfaced so the setter can
   *  see what they configured for each contact at a glance. */
  emergencyWaitHours?: number;
  emergencyReleaseAt?: string;
  resetExpiresAt?: string;
}

export interface ListRecoveryContactsResponse {
  contacts: RecoveryContactSummary[];
}

export interface InboundRecoveryRelationship {
  protectedUserId: string;
  protectedEmail: string;
  state: RecoveryContactState;
  createdAt: string;
  acceptedAt?: string;
  /** What this contact will have to wait between emergency-request and key
   *  fetch (hours). Surfaced so the contact's UI can show the realistic
   *  delay rather than always claiming the default 7 days. */
  emergencyWaitHours?: number;
  emergencyInitiatedAt?: string;
  /** The protected user's Ed25519 identity public key, base64url(32). Present
   *  once they have published one. The contact's client needs it to compute the
   *  SAME pairwise safety words the designator sees — without it the contact has
   *  nothing to compare against and the out-of-band check cannot be performed at
   *  all. Absent for a pre-PKA protected user, which the UI reports honestly
   *  rather than silently skipping verification. */
  protectedIdentityPublicKey?: string;
  /** The protected user's self-signature over their account key, so the contact
   *  can authenticate the identity key rather than trusting the server's copy. */
  protectedAccountPublicKeySig?: SignedKey;
  emergencyReleaseAt?: string;
}

/**
 * Someone has reached the verify step of designating this user, but has not
 * created the designation yet — it does not exist until they finish comparing
 * words. Surfaced so the contact gets an in-app prompt at the moment the
 * comparison needs to happen, rather than only an email.
 *
 * Short-lived by construction: the underlying row carries the same one-hour TTL
 * as the notification rate limit it doubles as.
 */
export interface PendingVerification {
  protectedUserId: string;
  protectedEmail: string;
  requestedAt: string;
}

export interface ListInboundRecoveryResponse {
  relationships: InboundRecoveryRelationship[];
  /** Absent on older servers — treat as empty, never as "none pending". */
  pendingVerifications?: PendingVerification[];
}

export interface ApproveResetRequest {
  resetWrappedVaultKey: string;
  resetWrappedAccountPrivateKey: string;
  /** Layer 2b: the 6-digit code the requester read to the contact out-of-band.
   *  The server verifies it (attempt-capped) before approving the reset. */
  verificationCode: string;
}

export interface RecoveryWrappedVaultKeyResponse {
  wrappedVaultKey: string;
}

export interface RecoveryAuditEntry {
  timestamp: string;
  action: RecoveryAuditAction;
  actorUserId: string;
  contactUserId: string;
}

export interface RecoveryAuditResponse {
  entries: RecoveryAuditEntry[];
}

// ─── Auth Lambda forgot-password endpoints ────────────────────────────────────

export interface RecoveryLookupRequest {
  email: string;
}

/**
 * The recovery-lookup response hides full contact emails behind an opaque,
 * short-TTL, single-purpose handle.
 *
 * - `contactHandle` is a signed token minted by `/auth/recovery/lookup` that
 *   binds (protectedUser, contact). The client passes it back in
 *   `RecoveryRequestStartRequest`; the server verifies it (integrity +
 *   expiry). It replaces the old raw Cognito sub — it is not a stable
 *   identifier and cannot be forged without the server secret.
 * - `maskedEmail` is server-formatted via `maskEmail(...)` — minimum info
 *   to differentiate sibling contacts (e.g. `ada…@gm…`).
 */
export interface RecoveryContactOption {
  contactHandle: string;
  maskedEmail: string;
}

export interface RecoveryLookupResponse {
  contacts: RecoveryContactOption[];
}

/**
 * `POST /auth/recovery/request-code` — emails an 8-digit possession code to the
 * account address. Enumeration-safe: the response is identical whether or not
 * the account exists.
 */
export interface RecoveryRequestCodeRequest {
  email: string;
}
export interface RecoveryRequestCodeResponse {
  sent: boolean;
}

export interface RecoveryRequestStartRequest {
  email: string;
  /** 8-digit possession code emailed to the account address by
   *  `/auth/recovery/request-code` — proves the requester controls the inbox. */
  code: string;
  /** Opaque signed handle from `/auth/recovery/lookup` (replaces the old raw
   *  contactId); the server verifies it to resolve the contact relationship. */
  contactHandle: string;
  ephPubReset: string; // base64url X25519 pub
}

export interface RecoveryRequestStartResponse {
  recoverySessionToken: string;
  /** Layer 2b: a 6-digit code the requester's client shows and reads to the
   *  contact out-of-band; the contact types it on approve. */
  verificationCode: string;
}

/**
 * `POST /auth/recovery/deny` — the account owner's one-click cancel, authorized
 * by a signed token from the "someone requested recovery of your account" email.
 * Reverts an in-flight reset and discards the requester's ephemeral key.
 */
export interface RecoveryDenyRequest {
  token: string;
}
export interface RecoveryDenyResponse {
  denied: boolean;
}

export interface RecoveryStatusResponse {
  state: RecoveryContactState;
  resetWrappedVaultKey?: string;
  resetWrappedAccountPrivateKey?: string;
  resetExpiresAt?: string;
}

export interface RecoveryFinishRequest {
  newOpaqueRecord: string;
  // The vault key and account private key wraps must be posted as separate
  // ciphertext + IV strings, NOT as a single base64(JSON({wrapped, iv}))
  // blob. The server stores them as four separate columns on the user's
  // PROFILE row, matching the encoding registration writes and the login
  // path reads. (Bundling them broke the next post-recovery OPAQUE login —
  // AES-GCM threw OperationError because the IV column stayed stale and
  // the ciphertext column contained JSON bytes instead of raw ciphertext.)
  newEncryptedVaultKey: string;
  newEncryptedVaultKeyIv: string;
  newWrappedAccountPrivateKey: string;
  newWrappedAccountPrivateKeyIv: string;
  newLocalKdfSalt: string;
}

// Recovery-specific replacement for /auth/opaque/register/start used by
// ForgotPasswordPage.finalize(): the regular register-start 409s for an
// already-registered email; /auth/recovery/opaque-start runs the same
// OPAQUE createRegistrationResponse but is gated by the recovery session
// token + RESET_APPROVED state instead of by "email not yet taken".
export interface RecoveryOpaqueStartRequest {
  registrationRequest: string;
}

export interface RecoveryOpaqueStartResponse {
  registrationResponse: string;
}

export interface RecoveryResetPackageResponse {
  resetEphemeralPub: string;
  wrappedVaultKey: string;
  wrappedAccountPrivateKey: string;
}

export interface RecoveryFinishResponse {
  userId: string;
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresIn: number;
}
